import type * as Ort from 'onnxruntime-web/wasm';

const SILERO_RATE = 16000;
const SILERO_WINDOW = 512;
const SILERO_CONTEXT = 64;
const STATE_SIZE = 2 * 128;

const MODEL_URL = `${process.env.PUBLIC_URL || ''}/vad/silero_vad.onnx`;

let sharedSession: Promise<Ort.InferenceSession> | null = null;
let ortApi: typeof Ort | null = null;
let sharedVad: SileroBargeVad | null = null;
let warmPromise: Promise<boolean> | null = null;

export function getSharedSileroBargeVad(): SileroBargeVad {
  if (!sharedVad) sharedVad = new SileroBargeVad(MODEL_URL, 0.5);
  return sharedVad;
}

/** One warmup for the page. A later call retries only after this one has failed. */
export function warmSileroBargeVad(): Promise<boolean> {
  if (!warmPromise) {
    warmPromise = getSharedSileroBargeVad()
      .warmup()
      .then(() => true)
      .catch((err: unknown) => {
        warmPromise = null;
        console.error('Silero barge-in warmup failed', err);
        return false;
      });
  }
  return warmPromise;
}

function loadSession(modelUrl: string): Promise<Ort.InferenceSession> {
  if (!sharedSession) {
    sharedSession = import('onnxruntime-web/wasm').then((ort) => {
      ortApi = ort;
      ort.env.wasm.numThreads = 1;
      ort.env.wasm.wasmPaths = `${process.env.PUBLIC_URL || ''}/ort/`;
      return ort.InferenceSession.create(modelUrl, {
        executionProviders: ['wasm'],
      });
    }).catch((err: unknown) => {
      sharedSession = null;
      throw err;
    });
  }
  return sharedSession;
}

/** Silero v5 speech check used only to decide barge-in. */
export class SileroBargeVad {
  private session: Ort.InferenceSession | null = null;
  private loading: Promise<void> | null = null;
  private state = new Float32Array(STATE_SIZE);
  private context = new Float32Array(SILERO_CONTEXT);
  private pending = new Float32Array(0);
  private threshold: number;

  constructor(
    private readonly modelUrl: string,
    threshold: number,
  ) {
    this.threshold = threshold;
  }

  setThreshold(threshold: number): void {
    this.threshold = threshold;
  }

  start(): void {
    if (!this.loading) {
      this.loading = this.load().catch((err: unknown) => {
        this.loading = null;
        this.session = null;
        console.error('Silero barge-in model failed to load', err);
      });
    }
  }

  /** Load the shared session, run one silent window, then clear VAD state. */
  async warmup(): Promise<void> {
    try {
      if (!this.loading) this.loading = this.load();
      await this.loading;
      if (!this.session) throw new Error('Silero barge-in model failed to load');
      await this.isSpeech(new Float32Array(SILERO_WINDOW));
      this.reset();
    } catch (err) {
      this.loading = null;
      this.session = null;
      throw err;
    }
  }

  reset(): void {
    this.state.fill(0);
    this.context.fill(0);
    this.pending = new Float32Array(0);
  }

  /** True when any full window in this callback is speech. False while the model is still loading. */
  async isSpeech(samples16k: Float32Array): Promise<boolean> {
    if (!this.session) {
      if (this.loading) await this.loading;
      if (!this.session) return false;
    }
    const merged = new Float32Array(this.pending.length + samples16k.length);
    merged.set(this.pending, 0);
    merged.set(samples16k, this.pending.length);
    let offset = 0;
    let speech = false;
    while (merged.length - offset >= SILERO_WINDOW) {
      const prob = await this.runWindow(merged.subarray(offset, offset + SILERO_WINDOW));
      if (prob >= this.threshold) speech = true;
      offset += SILERO_WINDOW;
    }
    this.pending = merged.slice(offset);
    return speech;
  }

  private async load(): Promise<void> {
    this.session = await loadSession(this.modelUrl);
  }

  private async runWindow(window: Float32Array): Promise<number> {
    const input = new Float32Array(SILERO_CONTEXT + SILERO_WINDOW);
    input.set(this.context, 0);
    input.set(window, SILERO_CONTEXT);
    const ort = ortApi;
    if (!ort || !this.session) return 0;
    const out = await this.session.run({
      input: new ort.Tensor('float32', input, [1, input.length]),
      state: new ort.Tensor('float32', this.state, [2, 1, 128]),
      sr: new ort.Tensor('int64', BigInt64Array.from([BigInt(SILERO_RATE)]), []),
    });
    const prob = Number(out.output.data[0]);
    this.state = new Float32Array(out.stateN.data as Float32Array);
    this.context = input.slice(input.length - SILERO_CONTEXT);
    return prob;
  }
}
